<script setup>
/**
 * Histórico de movimentações do contato (marcação/desmarcação de Número da
 * Equipe e demais eventos registrados), em ordem cronológica inversa.
 */
import { onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import TeamNumbersAPI from 'dashboard/api/aceleres/teamNumbers';

const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const events = ref([]);
const isLoading = ref(false);

const ACTION_LABELS = {
  team_number_marked: 'Marcado como Número da Equipe',
  team_number_unmarked: 'Marcação removida',
  contact_created: 'Contato criado',
  contact_updated: 'Contato atualizado',
};

const STATUS_LABELS = {
  active: 'Ativo',
  inactive: 'Inativo',
};

const actionLabel = event => ACTION_LABELS[event.action] || event.action;
const statusLabel = value => STATUS_LABELS[value] || value || '—';

const formatDate = value =>
  value
    ? messageTimestamp(new Date(value).getTime() / 1000, 'dd/MM/yyyy HH:mm')
    : '—';

const fetchHistory = async () => {
  isLoading.value = true;
  try {
    const { data } = await TeamNumbersAPI.history(props.contact.id);
    events.value = data.payload || [];
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        'Não foi possível carregar o histórico do contato.'
    );
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchHistory);
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black2"
    @click.self="emit('close')"
  >
    <div
      class="w-[36rem] max-h-[80vh] overflow-auto rounded-xl bg-n-solid-1 border border-n-strong p-6 flex flex-col gap-4"
    >
      <div class="flex items-start justify-between gap-4">
        <div>
          <h3 class="text-base font-medium text-n-slate-12">
            Histórico — {{ contact.name }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ contact.phone_number || contact.email || '' }}
          </p>
        </div>
        <button
          class="text-n-slate-11 hover:text-n-slate-12"
          aria-label="Fechar"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>

      <p v-if="isLoading" class="text-sm text-n-slate-11">Carregando...</p>
      <p v-else-if="!events.length" class="text-sm text-n-slate-11">
        Nenhuma movimentação registrada.
      </p>

      <ul v-else class="flex flex-col gap-3">
        <li
          v-for="event in events"
          :key="event.id"
          class="flex flex-col gap-1 pb-3 border-b border-n-weak last:border-0"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ actionLabel(event) }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ formatDate(event.created_at) }} ·
            {{ event.user?.name || 'Sistema' }}
            <template v-if="event.source"
              >· origem: {{ event.source }}</template
            >
          </span>
          <span
            v-if="event.previous_value || event.new_value"
            class="text-xs text-n-slate-11"
          >
            {{ statusLabel(event.previous_value) }} →
            {{ statusLabel(event.new_value) }}
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>
